export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function getTodayInputValue() {
  return new Date().toISOString().split("T")[0];
}

export function shiftDateInputValue(value, delta) {
  const base = value ? new Date(`${value}T12:00:00`) : new Date();
  base.setDate(base.getDate() + delta);
  return base.toISOString().split("T")[0];
}

export function formatDisplayDate(value) {
  if (!value) return "Date non définie";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "Date non définie";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function safeFormatBirthDate(value) {
  if (!value) return "Non renseignée";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Non renseignée";
  return date.toLocaleDateString("fr-FR");
}

export function getGroupDojoId(group) {
  return String(group?.dojo?.id ?? group?.dojo_id ?? "");
}

export function buildDojoGroupsMap(dojos = [], groups = []) {
  const map = {};

  dojos.forEach((dojo) => {
    map[String(dojo.id)] = [];
  });

  groups.forEach((group) => {
    const dojoId = getGroupDojoId(group);
    if (!dojoId) return;

    if (!map[dojoId]) {
      map[dojoId] = [];
    }

    map[dojoId].push(group);
  });

  return map;
}

export function getDefaultAdherentAvatar(adherent) {
  if (adherent?.photo_url) return adherent.photo_url;

  if (adherent?.genre === "Masculin") return "/img/judoka_avatar.png";
  if (adherent?.genre === "Féminin") return "/img/judokate_avatar.png";

  return "/img/default-avatar.png";
}

export function getInitials(adherent) {
  const nom = adherent?.nom?.[0] || "";
  const prenom = adherent?.prenom?.[0] || "";
  return `${prenom}${nom}`.toUpperCase() || "AD";
}

export function getAttendanceValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.presence || value.status || value.value || "";
}

export function normalizeAttendancePayload(raw) {
  if (!raw) return {};

  const source =
    raw?.presences && typeof raw.presences === "object" ? raw.presences : raw;

  if (!source || typeof source !== "object") return {};

  return Object.entries(source).reduce((acc, [key, value]) => {
    acc[String(key)] = getAttendanceValue(value);
    return acc;
  }, {});
}

export function getAttendanceStatusMeta(status) {
  switch (status) {
    case "Présent":
      return {
        label: "Présent",
        badgeClass:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
        cardClass:
          "border-emerald-200 dark:border-emerald-500/20",
      };

    case "Absent":
      return {
        label: "Absent",
        badgeClass:
          "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300",
        cardClass:
          "border-red-200 dark:border-red-500/20",
      };

    case "Malade/Blessé":
      return {
        label: "Malade/Blessé",
        badgeClass:
          "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300",
        cardClass:
          "border-amber-200 dark:border-amber-500/20",
      };

    default:
      return {
        label: "Non pointé",
        badgeClass:
          "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        cardClass:
          "border-slate-200 dark:border-slate-800",
      };
  }
}

export function getAdherentStatusMeta(status) {
  switch (status) {
    case "Inscrit":
      return {
        label: "Inscrit",
        className:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/10 dark:text-yellow-300",
      };

    case "Cours d'Essai":
      return {
        label: "Cours d'Essai",
        className:
          "bg-pink-100 text-pink-700 dark:bg-pink-500/10 dark:text-pink-300",
      };

    case "Licencié":
      return {
        label: "Licencié",
        className:
          "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300",
      };

    case "Licencié hors club":
      return {
        label: "Licencié hors club",
        className:
          "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300",
      };

    case "Non inscrit":
      return {
        label: "Non inscrit",
        className:
          "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300",
      };

    case "Arrêt":
      return {
        label: "Arrêt",
        className:
          "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
      };

    default:
      return {
        label: status || "Non défini",
        className:
          "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
      };
  }
}

export function getGroupAccentClass(color) {
  const map = {
    "bg-green-400": "border-green-400",
    "bg-red-400": "border-red-400",
    "bg-blue-400": "border-blue-400",
    "bg-yellow-400": "border-yellow-400",
    "bg-orange-400": "border-orange-400",
    "bg-purple-400": "border-purple-400",
    "bg-pink-400": "border-pink-400",
    "bg-indigo-400": "border-indigo-400",
    "bg-gray-400": "border-gray-400",
    "bg-teal-400": "border-teal-400",
    "bg-cyan-400": "border-cyan-400",
    "bg-black": "border-slate-900 dark:border-slate-200",
    "bg-white": "border-slate-300 dark:border-slate-600",
    "bg-transparent": "border-slate-300 dark:border-slate-600",
  };

  return map[color] || "border-slate-300 dark:border-slate-700";
}